import { useMemo } from 'react';
import { MeasurementMarkerDto } from 'types/models/SessionsPageData';
import ChartHint from './ChartHint';
import { t } from 'utilities/TranslationUtils';
import { darkTooltip, glassBucketBarStyle, calculatePercentYAxisRange } from 'utilities/chartUtils';
import { buildKicksPerSessionBucketData } from './playerPerformanceUtils';
import GrowChart from './GrowChart';

interface BucketDef {
  labelKey: string;
  min: number;
  max: number;
}

const BUCKETS: BucketDef[] = [
  { labelKey: 'playerPerformance.kicksBucket_1_5',    min: 1,  max: 5  },
  { labelKey: 'playerPerformance.kicksBucket_6_10',   min: 6,  max: 10 },
  { labelKey: 'playerPerformance.kicksBucket_11_15',  min: 11, max: 15 },
  { labelKey: 'playerPerformance.kicksBucket_16_20',  min: 16, max: 20 },
  { labelKey: 'playerPerformance.kicksBucket_21plus', min: 21, max: Infinity },
];


interface KicksPerSessionGoalRateChartProps {
  measurements: MeasurementMarkerDto[];
}

const KicksPerSessionGoalRateChart: React.FunctionComponent<KicksPerSessionGoalRateChartProps> = ({
  measurements,
}) => {
  const buckets = useMemo(
    () => BUCKETS.map((b) => ({
      label: t(b.labelKey, { min: b.min, max: b.max }),
      min: b.min,
      max: b.max,
    })),
    [],
  );
  const bucketData = useMemo(
    () => buildKicksPerSessionBucketData(measurements, buckets),
    [measurements, buckets],
  );
  const sessionCount = useMemo(() => {
    const ids = new Set<string>();
    for (const m of measurements) ids.add(m.sessionId);
    return ids.size;
  }, [measurements]);

  if (sessionCount === 0) return null;

  const yAxisRange = calculatePercentYAxisRange(bucketData.map((b) => b.onTargetRate));

  const option = {
    tooltip: {
      ...darkTooltip('item'),
      formatter: (params: { name: string; value: number; dataIndex: number }) => {
        const bucket = bucketData[params.dataIndex];
        if (bucket.count === 0) return `<b>${bucket.label}</b><br/>${t('playerPerformance.noSessionsInRange')}`;
        return [
          `<b>${bucket.label}</b>`,
          `${t('sessions.fieldSessionsLabel')}: ${bucket.count}`,
          `${t('playerPerformance.onTargetRate')}: ${bucket.onTargetRate.toFixed(1)}%`,
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'category' as const,
      data: bucketData.map((b) => b.label),
      axisLabel: { fontSize: 12, color: '#374151' },
      axisLine: { lineStyle: { color: '#E5E7EB' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      min: yAxisRange.min,
      max: yAxisRange.max,
      axisLabel: { fontSize: 12, color: '#374151', formatter: (v: number) => `${Math.min(100, v).toFixed(0)}%` },
      splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    grid: { left: 50, right: 20, top: 44, bottom: 30, containLabel: false },
    series: [
      {
        type: 'bar' as const,
        data: bucketData.map((b) => ({
          value: b.count === 0 ? 0 : b.onTargetRate,
          itemStyle: glassBucketBarStyle(),
        })),
        label: {
          show: true,
          position: 'top' as const,
          formatter: (params: { value: number; dataIndex: number }) => {
            const bucket = bucketData[params.dataIndex];
            return bucket.count === 0 ? t('playerPerformance.na') : `${params.value.toFixed(1)}%`;
          },
          fontSize: 13,
          fontWeight: 600,
          color: '#111827',
        },
      },
    ],
  };

  return (
    <div className='pp-section'>
      <h4>{t('playerPerformance.onTargetRateChartTitle')} <ChartHint hintKey='playerPerformance.chartHint_volume' /></h4>
      <GrowChart option={option} />
    </div>
  );
};

export default KicksPerSessionGoalRateChart;
